import React, { useState, useEffect } from "react";
import api from "../../../api/axiosConfig";
import Loader from "../../Shared/Loader";
import { showToast } from "../../Shared/Toaster";
import "./RescheduleConfigModal.css";

interface CenterOption {
  _id: string;
  name: string;
  city?: string;
  centerType?: string;
}

interface PromoOption {
  _id: string;
  code: string;
  code_type: string;
  fee_discount?: number;
}

interface CenterDateRangeItem {
  center: string;
  centerName?: string;
  fromDate: string;
  toDate: string;
}

interface Props {
  ticket?: any;
  clusterToDelete?: any;
  onClose: () => void;
  onSuccess?: () => void;
}

const RescheduleConfigModal: React.FC<Props> = ({ ticket, clusterToDelete, onClose, onSuccess }) => {
  const [centers, setCenters] = useState<CenterOption[]>([]);
  const [loadingCenters, setLoadingCenters] = useState<boolean>(true);
  const [selectedCenter, setSelectedCenter] = useState<string>("");

  // Date Ranges & Center Combinations
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [centerDateRanges, setCenterDateRanges] = useState<CenterDateRangeItem[]>([]);

  // Promocodes
  const [promoCodesList, setPromoCodesList] = useState<PromoOption[]>([]);
  const [venueCodesList, setVenueCodesList] = useState<PromoOption[]>([]);
  const [loadingCodes, setLoadingCodes] = useState<boolean>(true);

  const [selectedCouponCode, setSelectedCouponCode] = useState<string>("");
  const [selectedVenueCode, setSelectedVenueCode] = useState<string>("");

  const [addedCouponCodes, setAddedCouponCodes] = useState<string[]>([]);
  const [addedVenueCodes, setAddedVenueCodes] = useState<string[]>([]);

  // Expires At Date Time
  const [expiresAt, setExpiresAt] = useState<string>("");

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    fetchCenters();
    fetchPromocodes();
  }, []);

  // Pre-fill centerDateRanges if clusterToDelete is provided
  useEffect(() => {
    if (clusterToDelete && clusterToDelete.centerId) {
      setCenterDateRanges([
        {
          center: clusterToDelete.centerId,
          centerName: clusterToDelete.centerName || clusterToDelete.centerId,
          fromDate: clusterToDelete.startDate || "",
          toDate: clusterToDelete.endDate || "",
        },
      ]);
    }
  }, [clusterToDelete]);

  // Fetch Centers dropdown
  const fetchCenters = async () => {
    setLoadingCenters(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.center_api.get("/api/v1/centers", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = res.data?.data || res.data || [];
      setCenters(data);
    } catch (err) {
      console.error("Error fetching centers for reschedule modal:", err);
      showToast("Could not load centers.", "error");
    } finally {
      setLoadingCenters(false);
    }
  };

  // Fetch Promocodes & Venue codes
  const fetchPromocodes = async () => {
    setLoadingCodes(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const res = await api.payment_api.get("/admin/promocodes", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const codes: PromoOption[] = Array.isArray(res.data) ? res.data : [];

      const promos = codes.filter((c) => c.code_type === "PROMO");
      const venues = codes.filter((c) => c.code_type === "VENUE");

      setPromoCodesList(promos);
      setVenueCodesList(venues);
    } catch (err) {
      console.error("Error fetching promocodes for reschedule modal:", err);
    } finally {
      setLoadingCodes(false);
    }
  };

  // Add Center & Date Range combo
  const handleAddCenterDateRange = () => {
    if (!selectedCenter) {
      showToast("Please select a Center.", "error");
      return;
    }
    if (!fromDate || !toDate) {
      showToast("Please select both From Date and To Date.", "error");
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      showToast("From Date cannot be after To Date.", "error");
      return;
    }

    const centerObj = centers.find((c) => c._id === selectedCenter);
    const centerName = centerObj ? centerObj.name : selectedCenter;

    const exists = centerDateRanges.some(
      (item) =>
        item.center === selectedCenter &&
        item.fromDate === fromDate &&
        item.toDate === toDate
    );

    if (!exists) {
      setCenterDateRanges([
        ...centerDateRanges,
        { center: selectedCenter, centerName, fromDate, toDate },
      ]);
      setFromDate("");
      setToDate("");
    }
  };

  const handleRemoveCenterDateRange = (index: number) => {
    setCenterDateRanges(centerDateRanges.filter((_, i) => i !== index));
  };

  // Add Coupon Code
  const handleAddCoupon = () => {
    if (!selectedCouponCode) return;
    if (!addedCouponCodes.includes(selectedCouponCode)) {
      setAddedCouponCodes([...addedCouponCodes, selectedCouponCode]);
      setSelectedCouponCode("");
    }
  };

  const handleRemoveCoupon = (code: string) => {
    setAddedCouponCodes(addedCouponCodes.filter((c) => c !== code));
  };

  // Add Venue Code
  const handleAddVenue = () => {
    if (!selectedVenueCode) return;
    if (!addedVenueCodes.includes(selectedVenueCode)) {
      setAddedVenueCodes([...addedVenueCodes, selectedVenueCode]);
      setSelectedVenueCode("");
    }
  };

  const handleRemoveVenue = (code: string) => {
    setAddedVenueCodes(addedVenueCodes.filter((c) => c !== code));
  };

  // Submit Handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    let finalCenterDateRanges = centerDateRanges.map((item) => ({
      center_id: item.center,
      fromDate: item.fromDate,
      toDate: item.toDate,
    }));

    // Fallback if user selected a center & dates without clicking Add
    if (finalCenterDateRanges.length === 0 && selectedCenter && fromDate && toDate) {
      finalCenterDateRanges = [
        {
          center_id: selectedCenter,
          fromDate,
          toDate,
        },
      ];
    }

    if (finalCenterDateRanges.length === 0) {
      showToast("Please select at least one Center and Date Range.", "error");
      return;
    }

    if (!expiresAt) {
      showToast("Please select Expiry Date & Time (Expires At).", "error");
      return;
    }

    // Determine final coupon codes & venue codes
    let finalCoupons = [...addedCouponCodes];
    if (selectedCouponCode && !finalCoupons.includes(selectedCouponCode)) {
      finalCoupons.push(selectedCouponCode);
    }

    let finalVenues = [...addedVenueCodes];
    if (selectedVenueCode && !finalVenues.includes(selectedVenueCode)) {
      finalVenues.push(selectedVenueCode);
    }

    setIsSubmitting(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      const headers = token
        ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
        : { "Content-Type": "application/json" };

      let affectedBookingsPayload: any[] = [];

      if (clusterToDelete) {
        // Step 1: Call DELETE API for cluster
        const deletePayload = {
          appointment_cluster_id: clusterToDelete.appointmentId,
          reasonForChange: "",
        };

        const deleteRes = await api.appointment_api.delete(
          `/api/released-slots/${clusterToDelete.id}`,
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            data: deletePayload,
          }
        );

        // Step 2: Extract affected_bookings from DELETE response
        const rawAffected = deleteRes.data?.affected_bookings || [];
        affectedBookingsPayload = rawAffected.map((b: any) => ({
          userId: b.userId || "",
          bookingId: b.bookingId || "",
          email: b.userEmail || b.email || "",
        }));
      } else if (ticket) {
        const userId = ticket.parentAuth0Id || ticket.raisedBy || ticket.patientId || "";
        const bookingId = ticket.appointmentId || ticket.bookingId || ticket._id || "";
        const email = ticket.parentEmail || ticket.email || "";

        if (userId || bookingId) {
          affectedBookingsPayload.push({
            userId: userId || "",
            bookingId: bookingId || "",
            ...(email ? { email } : {}),
          });
        }
      }

      // Step 3: Call POST /api/reschedule-configs
      const payload: Record<string, any> = {
        centerDateRanges: finalCenterDateRanges,
        couponCodes: finalCoupons,
        venueCodes: finalVenues,
        affectedBookings: affectedBookingsPayload,
      };

      if (expiresAt) {
        payload.expiresAt = new Date(expiresAt).toISOString();
      }

      await api.appointment_api.post("/api/reschedule-configs", payload, { headers });

      showToast(
        clusterToDelete
          ? "Appointment cluster deleted and reschedule configuration created successfully!"
          : "Reschedule configuration created successfully!",
        "success"
      );

      if (onSuccess) onSuccess();
      onClose();
    } catch (err: any) {
      console.error("Error submitting reschedule config:", err);
      const errMsg =
        err.response?.data?.message || "Failed to complete reschedule configuration.";
      showToast(errMsg, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <div className="reschedule-modal-overlay" onClick={onClose}>
      <div
        className="reschedule-modal-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="reschedule-modal-header">
          <div>
            <h2>Create Reschedule Configuration</h2>
            <p className="subtitle">Set center screening dates and applicable promo/venue codes.</p>
          </div>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="reschedule-modal-form">
          {/* SECTION 1: CENTER & DATES */}
          <div className="config-section-card">
            <h4 className="section-card-title">1. Center & Screening Date Range</h4>

            {/* Center Selector */}
            <div className="form-group">
              <label>
                Select Center <span className="required">*</span>
              </label>
              <div className="select-wrapper">
                <select
                  value={selectedCenter}
                  onChange={(e) => setSelectedCenter(e.target.value)}
                  disabled={loadingCenters}
                >
                  <option value="">Choose Center...</option>
                  {centers.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name} {c.city ? `(${c.city})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date Range Inputs */}
            <div className="form-row-dates">
              <div className="form-group flex-1">
                <label>
                  From Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  min={todayStr}
                  className="modern-date-input"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              <div className="form-group flex-1">
                <label>
                  To Date <span className="required">*</span>
                </label>
                <input
                  type="date"
                  className="modern-date-input"
                  min={fromDate || todayStr}
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>
            </div>

            <button
              type="button"
              className="btn-add-primary"
              onClick={handleAddCenterDateRange}
            >
              + Add Center & Date Range
            </button>

            {/* Display Added Center Date Ranges */}
            {centerDateRanges.length > 0 && (
              <div className="added-items-tags">
                {centerDateRanges.map((item, idx) => (
                  <span key={idx} className="item-tag center-date">
                    📍 <strong>{item.centerName || item.center}:</strong> {item.fromDate} &rarr; {item.toDate}
                    <button
                      type="button"
                      onClick={() => handleRemoveCenterDateRange(idx)}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* SECTION 2: PROMO & VENUE CODES */}
          <div className="config-section-card">
            <h4 className="section-card-title">2. Applicable Discounts & Codes (Optional)</h4>

            <div className="form-row-codes">
              {/* Coupon Dropdown */}
              <div className="form-group flex-1">
                <label>Coupon Code</label>
                <div className="select-wrapper">
                  <select
                    value={selectedCouponCode}
                    onChange={(e) => setSelectedCouponCode(e.target.value)}
                    disabled={loadingCodes}
                  >
                    <option value="">Select Promo Code...</option>
                    {promoCodesList.map((p) => (
                      <option key={p._id} value={p.code}>
                        {p.code} {p.fee_discount ? `(₹${p.fee_discount} OFF)` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Venue Dropdown */}
              <div className="form-group flex-1">
                <label>Venue Code</label>
                <div className="select-wrapper">
                  <select
                    value={selectedVenueCode}
                    onChange={(e) => setSelectedVenueCode(e.target.value)}
                    disabled={loadingCodes}
                  >
                    <option value="">Select Venue Code...</option>
                    {venueCodesList.map((v) => (
                      <option key={v._id} value={v.code}>
                        {v.code} {v.fee_discount ? `(₹${v.fee_discount} OFF)` : ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="btn-add-secondary"
              onClick={() => {
                handleAddCoupon();
                handleAddVenue();
              }}
            >
              + Add Selected Code(s)
            </button>

            {/* Display Added Promo & Venue Code Tags */}
            {(addedCouponCodes.length > 0 || addedVenueCodes.length > 0) && (
              <div className="added-items-tags">
                {addedCouponCodes.map((code) => (
                  <span key={code} className="item-tag coupon">
                    🎟 <strong>Coupon:</strong> {code}
                    <button type="button" onClick={() => handleRemoveCoupon(code)}>
                      ×
                    </button>
                  </span>
                ))}
                {addedVenueCodes.map((code) => (
                  <span key={code} className="item-tag venue">
                    🏷 <strong>Venue:</strong> {code}
                    <button type="button" onClick={() => handleRemoveVenue(code)}>
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* SECTION 3: CONFIGURATION EXPIRY (EXPIRES AT) */}
          <div className="config-section-card">
            <h4 className="section-card-title">3. Expiry Date & Time (Expires At)</h4>

            <div className="form-group">
              <label>
                Expires At <span className="required">*</span>
              </label>
              <input
                type="datetime-local"
                className="modern-date-input"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
              <p className="helper-text">
                Select the expiration date and time for this reschedule configuration.
              </p>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="reschedule-modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader inline size="small" text="Submitting..." />
              ) : (
                <>
                  <span className="rocket-icon"></span> Submit Reschedule Config
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RescheduleConfigModal;
